import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  ToastBar,
  useToaster,
  type DefaultToastOptions,
  type ToastPosition,
} from 'react-hot-toast';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';

const DEFAULT_OFFSET = 16;
/** Modallar ve açılır menülerin (z-[200]) üzerinde kalması için */
const TOASTER_Z_INDEX = 99999;

const getPositionStyle = (
  position: ToastPosition,
  offset: number
): React.CSSProperties => {
  const top = position.includes('top');
  const verticalStyle: React.CSSProperties = top ? { top: 0 } : { bottom: 0 };
  const horizontalStyle: React.CSSProperties = position.includes('center')
    ? { justifyContent: 'center' }
    : position.includes('right')
      ? { justifyContent: 'flex-end' }
      : {};

  return {
    left: 0,
    right: 0,
    display: 'flex',
    position: 'absolute',
    transition: 'all 230ms cubic-bezier(.21,1.02,.73,1)',
    transform: `translateY(${offset * (top ? 1 : -1)}px)`,
    ...verticalStyle,
    ...horizontalStyle,
  };
};

function ToastSlot({
  id,
  visible,
  positionStyle,
  onHeightUpdate,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  id: string;
  visible: boolean;
  positionStyle: React.CSSProperties;
  onHeightUpdate: (id: string, height: number) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  children: React.ReactNode;
}) {
  const elRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const updateHeight = () => {
      onHeightUpdate(id, el.getBoundingClientRect().height);
    };
    updateHeight();
    const observer = new MutationObserver(updateHeight);
    observer.observe(el, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, [id, onHeightUpdate]);

  return (
    <div
      ref={elRef}
      style={positionStyle}
      className={visible ? '[&>*]:pointer-events-auto' : ''}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
}

type Props = {
  position?: ToastPosition;
  toastOptions?: DefaultToastOptions;
  gutter?: number;
};

const AppToaster: React.FC<Props> = ({
  position = 'top-right',
  toastOptions,
  gutter = 12,
}) => {
  const { toasts, handlers } = useToaster(toastOptions);

  const toaster = (
    <div
      id="_rht_toaster"
      aria-live="polite"
      style={{
        position: 'fixed',
        zIndex: TOASTER_Z_INDEX,
        top: DEFAULT_OFFSET,
        right: DEFAULT_OFFSET,
        left: 'auto',
        bottom: 'auto',
        width: 'min(420px, calc(100vw - 32px))',
        pointerEvents: 'none',
      }}
      onMouseEnter={handlers.startPause}
      onMouseLeave={handlers.endPause}
    >
      {toasts.map((t) => {
        const toastPosition = t.position || position;
        const offset = handlers.calculateOffset(t, {
          gutter,
          defaultPosition: position,
        });
        const positionStyle = getPositionStyle(toastPosition, offset);

        return (
          <ToastSlot
            key={t.id}
            id={t.id}
            visible={t.visible}
            positionStyle={positionStyle}
            onHeightUpdate={handlers.updateHeight}
            onMouseEnter={handlers.startPause}
            onMouseLeave={handlers.endPause}
          >
            <div className="relative max-w-full">
              <ToastBar toast={t} position={toastPosition} />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(t.id);
                }}
                className="absolute right-1 top-1 rounded-md p-1 text-gray-400 hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-gray-200"
                aria-label="Bildirimi kapat"
              >
                <X size={16} strokeWidth={2} />
              </button>
            </div>
          </ToastSlot>
        );
      })}
    </div>
  );

  if (typeof document === 'undefined') return toaster;

  return createPortal(toaster, document.body);
};

export default AppToaster;
